import DesignTokens from '@tableau/design-tokens';
import { LoadingBaseIcon } from '@tableau/icons/LoadingBaseIcon';
import { TrashBaseIcon } from '@tableau/icons/TrashBaseIcon';
import { ButtonType, ButtonWidget, CheckBoxWithLabelWidget, InlineAlertType, InlineAlertWidget, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

import { IpAllowList } from '../serverApi/types/IpAllowList';
import { ipAllowlistIpTextField, ipAllowlistIpTextFieldNewlyAdded } from '../styles/IpAllowlistStyles';
import { IpValidationUtil } from '../utils/IpValidationUtil';
import { AddNewIpButton } from './AddNewIpButton';
import { openBulkEditDialog } from './BulkEditDialog';
import { openSaveConfirmationDialog } from './SaveConfirmationDialog';

// Constant for the number of IP addresses to show initially
const INITIAL_IP_DISPLAY_COUNT = 10;

interface IpAllowlistViewProps {
    ipAllowList?: IpAllowList;
    requesterIp?: string;
    isLoading: boolean;
    isSaving: boolean;
    fetchError?: Error;
    onSave: (allowedIPs: string[]) => Promise<void>;
}

interface IpAllowlistViewState {
    enabled: boolean;
    allowedIPs: string[];
    addedIPs: string[];
    originalAllowedIPs: string[];
    ipValidationResults: boolean[];
    isExpanded: boolean;
    lastIndexOfExistingIPs: number;
}

export class IpAllowlistView extends React.PureComponent<IpAllowlistViewProps, IpAllowlistViewState> {
    public constructor(props: IpAllowlistViewProps) {
        super(props);
        this.state = {
            enabled: false,
            allowedIPs: [],
            addedIPs: [],
            originalAllowedIPs: [],
            ipValidationResults: [],
            isExpanded: false,
            lastIndexOfExistingIPs: 0,
        };
    }

    public componentDidUpdate(prevProps: IpAllowlistViewProps): void {
        if (prevProps.ipAllowList !== this.props.ipAllowList) {
            const newPropsAllowedIPs = this.props.ipAllowList?.allowedIPs || [];
            const originalHasIPs = newPropsAllowedIPs.length > 0;
            const lastIndexOfExistingIPs = newPropsAllowedIPs.length - 1;
            this.setState({
                enabled: originalHasIPs,
                allowedIPs: newPropsAllowedIPs,
                addedIPs: [],
                originalAllowedIPs: Array.from(newPropsAllowedIPs),
                ipValidationResults: Array(newPropsAllowedIPs.length).fill(true),
                isExpanded: false,
                lastIndexOfExistingIPs: lastIndexOfExistingIPs,
            });
        }
    }

    handleEnableChange = (isEnabled: boolean) => {
        if (isEnabled) {
            if (this.state.allowedIPs.length > 0) {
                this.setState({
                    enabled: true
                });
            } else {
                this.setState({
                    enabled: true,
                    allowedIPs: ['']
                });
            }
        } else if (!isEnabled) {
            // If disabling, just set enabled to false, keep IPs as they are
            this.setState({
                enabled: false
            });
        }
    };

    handleIpChange = (index: number, value: string) => {
        const allowedIPs = Array.from(this.state.allowedIPs);
        allowedIPs[index] = value;

        // Validate the IP and update validation results
        const isValid = IpValidationUtil.validateIpAddress(value);
        this.updateIpValidation(index, isValid);

        this.setState({ allowedIPs });
    };

    handleDeleteIp = async (index: number) => {
        const allowedIPs = Array.from(this.state.allowedIPs);
        const ipValidationResults = Array.from(this.state.ipValidationResults);

        allowedIPs.splice(index, 1);
        ipValidationResults.splice(index, 1);

        // Update lastIndexOfExistingIPs if we're deleting an original IP
        let newLastIndexOfExistingIPs = this.state.lastIndexOfExistingIPs;
        if (index <= this.state.lastIndexOfExistingIPs) {
            newLastIndexOfExistingIPs = Math.max(0, this.state.lastIndexOfExistingIPs - 1);
        }

        this.setState({
            allowedIPs,
            ipValidationResults,
            lastIndexOfExistingIPs: newLastIndexOfExistingIPs
        });
    };

    handleAddIp = () => {
        const newAllowedIPs = this.state.allowedIPs.concat(['']);
        const newValidationResults = this.state.ipValidationResults.concat([true]);
        this.setState({
            allowedIPs: newAllowedIPs,
            ipValidationResults: newValidationResults
        });
    };

    handleBulkEdit = () => {
        openBulkEditDialog(this.state.originalAllowedIPs, this.handleBulkEditSave);
    };

    handleBulkEditSave = async (allowedIPs: string[]) => {
        // Update the state with the new IPs from bulk edit
        this.setState({
            allowedIPs: allowedIPs,
            ipValidationResults: allowedIPs.map(ip => IpValidationUtil.validateIpAddress(ip))
        }, async () => {
            // Use the existing handleSave method to save the changes
            await this.handleSave();
        });
    };

    handleSave = async () => {
        // If disabled, clear the allowedIPs before saving
        let allowedIPsToSave = this.state.enabled ? this.state.allowedIPs : [];

        // Remove empty IPs before saving
        allowedIPsToSave = allowedIPsToSave.filter(ip => ip.trim() !== '');

        // Check if requester IP is covered by the allowed IP list
        const { requesterIp } = this.props;
        if (requesterIp && allowedIPsToSave.length > 0) {
            const isRequesterIpCovered = IpValidationUtil.isIpCovered(requesterIp, allowedIPsToSave);

            if (!isRequesterIpCovered) {
                // Show confirmation dialog
                openSaveConfirmationDialog(
                    Messages.ipAllowlistSaveConfirmationMessage({ IP_ADDRESS: requesterIp }),
                    async () => {
                        // Proceed with saving after confirmation
                        await this.props.onSave(allowedIPsToSave);
                    }
                );
                return;
            }
        }

        // Call the save function passed as prop and get the result
        await this.props.onSave(allowedIPsToSave);
    };

    handleCancel = () => {
        // Reset to original values
        const originalHasIPs = this.state.originalAllowedIPs.length > 0;
        this.setState({
            enabled: originalHasIPs,
            allowedIPs: Array.from(this.state.originalAllowedIPs),
            lastIndexOfExistingIPs: this.state.originalAllowedIPs.length - 1,
        }, () => {
            this.validateAllIps();
        });
    };

    handleToggleExpanded = () => {
        this.setState(prevState => ({
            isExpanded: !prevState.isExpanded
        }));
    };

    private hasChanges(): boolean {
        const { enabled, allowedIPs, originalAllowedIPs } = this.state;
        const originalEnabled = originalAllowedIPs.length > 0;
        if (!enabled && !originalEnabled) {
            return false;
        }

        // Filter out empty IPs for comparison
        const nonEmptyAllowedIPs = allowedIPs.filter(ip => ip.trim() !== '');
        const nonEmptyOriginalIPs = originalAllowedIPs.filter(ip => ip.trim() !== '');

        // Check if enabled state changed from original
        if (enabled !== originalEnabled) {
            if (!enabled || nonEmptyAllowedIPs.length > 0) {
                return true;
            }
        }

        // Check if number of non-empty IPs changed
        if (nonEmptyAllowedIPs.length !== nonEmptyOriginalIPs.length) {
            return true;
        }

        // Check if any non-empty IP values changed
        for (let i = 0; i < nonEmptyAllowedIPs.length; i++) {
            if (nonEmptyAllowedIPs[i] !== nonEmptyOriginalIPs[i]) {
                return true;
            }
        }

        return false;
    }

    private validateAllIps(): void {
        const ipValidationResults = this.state.allowedIPs.map(ip => IpValidationUtil.validateIpAddress(ip));
        this.setState({ ipValidationResults });
    }

    private updateIpValidation(index: number, isValid: boolean) {
        const ipValidationResults = Array.from(this.state.ipValidationResults);
        ipValidationResults[index] = isValid;
        this.setState({ ipValidationResults });
    }

    private hasInvalidIps(): boolean {
        return this.state.ipValidationResults.some((isValid, index) => {
            const ip = this.state.allowedIPs[index];
            return ip && ip.trim() !== '' && !isValid;
        });
    }

    private getDisplayedExistingIPs(): string[] {
        const { allowedIPs, isExpanded, lastIndexOfExistingIPs } = this.state;

        // For original IPs, apply the expand/collapse logic
        const originalIPs = allowedIPs.slice(0, lastIndexOfExistingIPs + 1);
        let displayedOriginalIPs: string[];

        if (isExpanded || originalIPs.length <= INITIAL_IP_DISPLAY_COUNT) {
            displayedOriginalIPs = originalIPs;
        } else {
            displayedOriginalIPs = originalIPs.slice(0, INITIAL_IP_DISPLAY_COUNT);
        }

        return displayedOriginalIPs;
    }

    private getDisplayedNewlyAddedIPs(): string[] {
        const { allowedIPs, lastIndexOfExistingIPs } = this.state;
        return allowedIPs.slice(lastIndexOfExistingIPs + 1);
    }

    public render() {
        const { isLoading, isSaving, fetchError: fetchError, requesterIp } = this.props;
        const { enabled, allowedIPs, isExpanded } = this.state;
        const deviceIp = requesterIp || '192.168.1.1'; // Use requesterIp if available, fallback to placeholder
        const hasChanges = this.hasChanges();
        const displayedIPs = this.getDisplayedExistingIPs();
        const newlyAddedIPs = this.getDisplayedNewlyAddedIPs();
        const originalIPsCount = this.state.lastIndexOfExistingIPs + 1;
        const hasMoreOriginalIPs = originalIPsCount > INITIAL_IP_DISPLAY_COUNT;
        const showToggleButton = hasMoreOriginalIPs;

        if (isLoading) {
            return <div>
                <LoadingBaseIcon
                    size={18}
                    alt={Messages.ipAllowlistLoading()}
                    state='Default'
                />
            </div>;
        }

        if (fetchError) {
            return <div>{Messages.ipAllowlistGetFailureMessage()}</div>;
        }

        return (
            <div >
                <div style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}>
                    <CheckBoxWithLabelWidget
                        testId='ip-allowlist-enable-checkbox'
                        label={Messages.ipAllowlistCeckboxTitle()}
                        checked={enabled}
                        handleChange={this.handleEnableChange}
                        tabIndex={-1}
                        removeLeftMargin={true}
                        disabled={isSaving}
                    />
                </div>
                {enabled &&
                (<div style={{ marginLeft: CommonStyles.Sizes.LargeSpace }}>
                    <div style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}>
                        <InlineAlertWidget
                            style={{marginBottom: CommonStyles.Sizes.SmallSpace, display: 'inline-flex'}}
                            children={Messages.ipAllowlistCurrentIpInfo({IP_ADDRESS: deviceIp})}
                            testId={'ip-allowlist-current-ip-info'}
                            type={InlineAlertType.Info} />
                     </div>
                     {allowedIPs.length > 0 && (
                        <div style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}>
                            {displayedIPs.length > 0 && (
                                <div style={{ marginBottom: CommonStyles.Sizes.SmallSpace, fontWeight: 500 }}>{Messages.ipAllowlistAllowedIpAddresses()}</div>
                            )}
                            {displayedIPs.map((ip, idx) => (
                                <div key={idx} style={{ marginBottom: CommonStyles.Sizes.DefaultSpace }}>
                                    <TextFieldWidget
                                        message={this.state.ipValidationResults[idx] === false ? Messages.ipAllowlistInvalidIpAddress() : ''}
                                        className={ipAllowlistIpTextField}
                                        testId={`ip-allowlist-ip-address-${idx}`}
                                        label=''
                                        size={35}
                                        value={ip}
                                        onChange={(e) => this.handleIpChange(idx, e.target.value)}
                                        disabled={isSaving}
                                        valid={!(this.state.ipValidationResults[idx] === false)}
                                        placeholder={Messages.ipAllowlistIpAddressFormat({IP: '192.168.1.1', IP_RANGE: '192.168.1.1/24'})}
                                    />
                                    <ButtonWidget
                                        style={{minWidth: 0, marginLeft: CommonStyles.Sizes.DefaultSpace, verticalAlign: 'middle'}}
                                        buttonType={ButtonType.Icon}
                                        testId={`ip-allowlist-ip-address-delete-button-${idx}`}
                                        density='high'
                                        onClick={() => this.handleDeleteIp(idx)}
                                        disabled={isSaving}
                                    >
                                        <TrashBaseIcon
                                            size={18}
                                            alt={Messages.ipAllowlistDeleteIpAddress()}
                                        />
                                    </ButtonWidget>
                                </div>
                            ))}
                            {showToggleButton && (
                                <div style={{ marginTop: CommonStyles.Sizes.SmallSpace }}>
                                    <ButtonWidget
                                        buttonType={ButtonType.Icon}
                                        testId='ip-allowlist-toggle-expanded-button'
                                        onClick={this.handleToggleExpanded}
                                        disabled={isSaving}
                                        style={{width: 235, fontWeight: 500, color: applyColor(DesignTokens.color.font.default.default, '#2e2e2e')}}
                                    >
                                        {isExpanded ? Messages.ipAllowlistShowLess() : Messages.ipAllowlistShowMore({COUNT: (this.state.lastIndexOfExistingIPs - INITIAL_IP_DISPLAY_COUNT + 1).toString()})}
                                    </ButtonWidget>
                                </div>
                            )}

                            {newlyAddedIPs.length > 0 && (
                                 <div style={{ marginBottom: CommonStyles.Sizes.SmallSpace, fontWeight: 500, marginTop: CommonStyles.Sizes.MediumSpace }}>{Messages.ipAllowlistNewlyAddedIpAddresses()}</div>
                            )}
                            {newlyAddedIPs.map((ip, idx) => {
                                const newIdx = idx + this.state.lastIndexOfExistingIPs + 1;
                                return (
                                <div key={newIdx} style={{ marginBottom: CommonStyles.Sizes.DefaultSpace }}>
                                    <TextFieldWidget
                                        message={this.state.ipValidationResults[newIdx] === false ? Messages.ipAllowlistInvalidIpAddress() : ''}
                                        className={ipAllowlistIpTextFieldNewlyAdded}
                                        testId={`ip-allowlist-ip-address-${newIdx}`}
                                        label=''
                                        size={35}
                                        value={ip}
                                        onChange={(e) => this.handleIpChange(newIdx, e.target.value)}
                                        disabled={isSaving}
                                        valid={!(this.state.ipValidationResults[newIdx] === false)}
                                        placeholder={Messages.ipAllowlistIpAddressFormat({IP: '192.168.1.1', IP_RANGE: '192.168.1.1/24'})}
                                    />
                                    <ButtonWidget
                                        style={{minWidth: 0, marginLeft: CommonStyles.Sizes.DefaultSpace, verticalAlign: 'middle'}}
                                        buttonType={ButtonType.Icon}
                                        testId={`ip-allowlist-ip-address-delete-button-${newIdx}`}
                                        density='high'
                                        onClick={() => this.handleDeleteIp(newIdx)}
                                        disabled={isSaving}
                                    >
                                        <TrashBaseIcon
                                            size={18}
                                            alt={Messages.ipAllowlistDeleteIpAddress()}
                                        />
                                    </ButtonWidget>
                                </div>
                                );
                            })}
                        </div>
                     )}
                     <div>
                        <AddNewIpButton
                            onAddIp={this.handleAddIp}
                            onBulkEdit={this.handleBulkEdit}
                            disabled={isSaving}
                        />
                    </div>
                </div>)}
                {hasChanges && (
                    <div style={{ marginTop: CommonStyles.Sizes.LineHeight, marginLeft: CommonStyles.Sizes.LargeSpace }}>
                        <ButtonWidget
                            buttonType={ButtonType.Outline}
                            testId='ip-allowlist-cancel-button'
                            onClick={this.handleCancel}
                            disabled={isSaving}
                            style={{marginRight: CommonStyles.Sizes.MediumSpace}}
                        >
                            {Messages.cancel()}
                        </ButtonWidget>
                        <ButtonWidget
                            buttonType={ButtonType.Primary}
                            testId='ip-allowlist-save-button'
                            onClick={this.handleSave}
                            disabled={isSaving || this.hasInvalidIps()}
                            style={{marginLeft: CommonStyles.Sizes.DefaultSpace}}
                        >
                            {Messages.save()}
                        </ButtonWidget>
                    </div>
                )}
            </div>
        );
    }
}